import React, { useState } from 'react';
import { format, subYears, isAfter } from 'date-fns';
import { getPrivacyRequirements, getAgeVerificationMessage } from '../../utils/privacyCompliance';
import { useLocation } from '../../hooks/useLocation';
import { trackEvent } from '../../utils/analytics';

interface AgeVerificationProps {
  onVerified: (birthDate: string, needsParentalConsent: boolean, state?: string) => void;
  onCancel: () => void;
}

export const AgeVerification: React.FC<AgeVerificationProps> = ({ onVerified, onCancel }) => {
  const [birthDate, setBirthDate] = useState('');
  const [error, setError] = useState('');
  const location = useLocation();

  // Get requirements based on location
  const requirements = getPrivacyRequirements(location.state);
  const verificationMessage = getAgeVerificationMessage(location.state);

  // Calculate dates
  const today = new Date();
  const minAgeDate = subYears(today, requirements.minAge);
  const parentalConsentDate = subYears(today, requirements.parentalConsentAge);
  const maxDate = format(today, 'yyyy-MM-dd'); // Can't be born in the future

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!birthDate) {
      setError('Please enter your date of birth');
      return;
    }

    const selectedDate = new Date(birthDate);

    // Check if user meets minimum age
    if (isAfter(selectedDate, minAgeDate)) {
      setError(`You must be at least ${requirements.minAge} years old to create an account`);
      const age = today.getFullYear() - selectedDate.getFullYear();
      trackEvent.ageVerified(age, location.state);
      return;
    }

    // Check if user needs parental consent
    const needsParentalConsent = isAfter(selectedDate, parentalConsentDate);
    const age = today.getFullYear() - selectedDate.getFullYear();

    trackEvent.ageVerified(age, location.state);
    if (needsParentalConsent) {
      trackEvent.parentalConsentRequired();
    }

    onVerified(birthDate, needsParentalConsent, location.state);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-rowing-blue bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-rowing-blue"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-rowing-gray-900">Age Verification Required</h2>
        <p className="mt-2 text-rowing-gray-600">
          To comply with privacy laws, we need to verify your age
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="birthDate"
            className="block text-sm font-medium text-rowing-gray-700 mb-2"
          >
            Date of Birth
          </label>
          <input
            id="birthDate"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            max={maxDate}
            required
            className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
          />
          <p className="mt-2 text-xs text-rowing-gray-500">{verificationMessage}</p>
          {location.state && (
            <p className="mt-1 text-xs text-rowing-gray-500">Location detected: {location.state}</p>
          )}
        </div>

        <div className="bg-rowing-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-rowing-gray-900 mb-2">Why we need this:</h3>
          <ul className="text-xs text-rowing-gray-600 space-y-1">
            <li>• To comply with COPPA and other privacy laws</li>
            <li>• To ensure age-appropriate features and content</li>
            <li>• To determine if parental consent is required</li>
            <li>• Your exact birth date is kept private and secure</li>
          </ul>
        </div>

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-rowing-gray-300 text-rowing-gray-700 rounded-lg hover:bg-rowing-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button type="submit" className="flex-1 btn-primary py-3">
            Verify Age
          </button>
        </div>
      </form>

      <div className="mt-6 text-center">
        <p className="text-xs text-rowing-gray-500">
          By continuing, you agree to our{' '}
          <a href="/privacy" className="text-rowing-blue hover:underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="/terms" className="text-rowing-blue hover:underline">
            Terms of Service
          </a>
        </p>
      </div>
    </div>
  );
};
